import { Link } from '@inertiajs/react'
import { motion } from 'motion/react'
import { ShoppingBag, Menu, X, User } from 'lucide-react'
import { useState } from 'react'
import { useCart } from '../lib/cart'
import { useT, useShared } from '../lib/i18n'
import { LanguageSwitcher } from './LanguageSwitcher'
import logoUrl from '../assets/logo.png'

interface NavLink {
  href: string
  label: string
}

export function StoreHeader() {
  const { count, openCart } = useCart()
  const t = useT()
  const { nav_categories, current_customer } = useShared()
  const [mobileOpen, setMobileOpen] = useState(false)

  const links: NavLink[] = [
    { href: '/', label: t('nav.home') },
    { href: '/c/all', label: t('nav.products') },
    ...(nav_categories ?? []).slice(0, 4).map((c) => ({ href: `/c/${c.slug}`, label: c.name })),
  ]

  return (
    <header className="sticky top-0 z-40 border-b border-ink-100 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex h-20 max-w-6xl items-center justify-between gap-4 px-4">
        <div className="flex items-center gap-3">
          <button
            className="md:hidden rounded-lg p-2 text-ink-700 hover:bg-ink-100"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="menu"
          >
            {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
          <Link href="/" aria-label={t('brand')}>
            <img src={logoUrl} alt={t('brand')} className="h-16 w-auto" />
          </Link>
        </div>

        <nav className="hidden items-center gap-7 md:flex">
          {links.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              className="text-sm font-bold text-ink-600 transition-colors hover:text-ink-900"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Link
            href={current_customer ? '/account' : '/login'}
            className="hidden items-center gap-1.5 rounded-xl p-2.5 text-sm font-bold text-ink-800 transition-colors hover:bg-ink-100 sm:flex"
            aria-label={current_customer ? t('nav.account') : t('nav.login')}
          >
            <User className="size-5" />
            <span className="max-w-24 truncate">
              {current_customer ? current_customer.name : t('nav.login')}
            </span>
          </Link>
          <button
            onClick={openCart}
            className="relative rounded-xl p-2.5 text-ink-800 transition-colors hover:bg-ink-100"
            aria-label={t('nav.cart')}
          >
            <ShoppingBag className="size-5" />
            {count > 0 && (
              <motion.span
                key={count}
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                className="absolute -end-0.5 -top-0.5 flex size-5 items-center justify-center rounded-full bg-accent-500 text-[11px] font-bold text-white"
              >
                {count}
              </motion.span>
            )}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="border-t border-ink-100 px-4 py-3 md:hidden">
          {links.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              onClick={() => setMobileOpen(false)}
              className="block py-2.5 text-sm font-medium text-ink-700"
            >
              {l.label}
            </Link>
          ))}
          <Link
            href={current_customer ? '/account' : '/login'}
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 border-t border-ink-100 py-2.5 text-sm font-bold text-ink-900"
          >
            <User className="size-4" />
            {current_customer ? current_customer.name : t('nav.login')}
          </Link>
        </nav>
      )}
    </header>
  )
}
